import { mapActions, mapGetters } from 'vuex';
import { BehaviorSubject } from 'rxjs';

import * as S from './styles';

import DropdownBox from '@components/Common/DropdownBox/defaultDropdown';
import SelectedBox from '@components/Common/DropdownBox/selectedBox';
import InfoLineIcon from '@svgIcons/widget_info.svg';

import {
    CHART_TYPES,
    COUNTRY_BREAKDOWN,
    GRAPH_FILTER_TYPES,
    LINE_CLAMP_DIR as _DIRECTIVE_CLAMP,
    GEO_MAP_TYPES,
    STATE_US_ONLY_BREAKDOWN,
} from '@consts';
import { DATE_GROUP_ROWS } from '@/utils/dashboard';

import RingLoader from '@components/Common/RingLoader';
import {
    MetricQuickControl,
    BreakdownQuickControl,
    ComboMetricQuickControl,
} from '../../reactOptions';

import breakdownTranslatorMixin from '@/mixins/translator/breakdown';
import metricTranslatorMixin from '@/mixins/translator/metric';

import { ITEM_STATUS } from '../styles';

import { EXPOSURE_KEY_CONFIG } from '../../reactOptions/quickControl/constants';
import { capitalize } from '@/utils/utils';
import {
    makeMetricFormat,
    makeBreakdownFormat,
    FIELD_KEY,
    pluckFieldKey,
} from '../../reactOptions/utils';
import { FIELD_PANEL_KEY } from '../../reactOptions/consts';

const {
    BAR,
    HORIZONTAL_BAR,
    LINE,
    COMBO_CHART,
    SCATTER,
    PIE,
    DONUT,
    WORD_CLOUD,
    COLOR_CLUSTERING,
    EMOJI_ANALYSIS,
    FUNNEL,
    CHOROPLETH_MAP,
} = CHART_TYPES;

const {
    GRAPH,
    YAXIS,
    XAXIS,
    BUBBLE_SIZE,
    COMBO_YAXIS_1,
    COMBO_YAXIS_2,
    COMBO_YAXIS_3,
    BREAKDOWN,
    DATE_BREAKDOWN,
    GEO_BREAKDOWN,
    GEO_MAP_TYPE,
} = GRAPH_FILTER_TYPES;

const SETTING_OPTIONS_MAP = {
    [BAR]: [GRAPH, YAXIS, DATE_BREAKDOWN, BREAKDOWN],
    [HORIZONTAL_BAR]: [GRAPH, YAXIS, DATE_BREAKDOWN, BREAKDOWN],
    [LINE]: [GRAPH, YAXIS, DATE_BREAKDOWN, BREAKDOWN],
    [COMBO_CHART]: [
        GRAPH,
        COMBO_YAXIS_1,
        COMBO_YAXIS_2,
        COMBO_YAXIS_3,
        BREAKDOWN,
    ],
    [SCATTER]: [GRAPH, YAXIS, XAXIS, BREAKDOWN, BUBBLE_SIZE],
    [PIE]: [GRAPH, YAXIS, BREAKDOWN],
    [DONUT]: [GRAPH, YAXIS, BREAKDOWN],
    [WORD_CLOUD]: [GRAPH, YAXIS],
    [COLOR_CLUSTERING]: [GRAPH, YAXIS, XAXIS],
    [EMOJI_ANALYSIS]: [GRAPH, YAXIS, XAXIS],
    [FUNNEL]: [GRAPH],
    [CHOROPLETH_MAP]: [GRAPH, YAXIS, GEO_BREAKDOWN, GEO_MAP_TYPE],
};

export const EMPTY_METRIC = {
    key: '',
};

const ALL_CHART_TYPES = Object.values(CHART_TYPES);

const DIRECTIVE_CLAMP = R.assocPath(
    ['directives', 0, 'value'],
    3,
    _DIRECTIVE_CLAMP
);

const GEO_BREAKDOWN_LIST = [COUNTRY_BREAKDOWN, STATE_US_ONLY_BREAKDOWN];

const METRIC_EXPOSURE_AXES = new Set([YAXIS, XAXIS, BUBBLE_SIZE]);

const COMBO_METRIC_EXPOSURE_AXES = new Set([
    COMBO_YAXIS_1,
    COMBO_YAXIS_2,
    COMBO_YAXIS_3,
]);

const BREAKDOWN_EXPOSURE_AXES = new Set([BREAKDOWN, DATE_BREAKDOWN]);

/**
 * exposureListKey → BehaviorSubject key (filter type) 매핑
 * EXPOSURE_KEY_CONFIG에서 역방향으로 도출
 * pendingExposureListKey(fieldListKey) → _axisSecondPanelVisibility$[filterKey]
 */
const EXPOSURE_LIST_KEY_TO_FILTER_KEY = Object.fromEntries(
    Object.entries(EXPOSURE_KEY_CONFIG).map(([filterKey, config]) => [
        config.fieldListKey,
        filterKey,
    ])
);

export default {
    name: 'PanelGraphWidgetOption',
    mixins: [breakdownTranslatorMixin, metricTranslatorMixin],
    props: {
        graphOptions: {
            type: Object,
            default() {
                return {};
            },
        },
        currentListByType: {
            type: Function,
            default: R.always([]),
        },
        editingWidget: {
            type: Object,
            default: {},
        },
        breakdownList: {
            type: Array,
            default: [],
        },
        secondPanelLoading: {
            type: Boolean,
            default: false,
        },
        /**
         * Exposure field list from WidgetSettingPanel (unified)
         * { primaryMetric: [...], secondaryMetric: [...], bubbleSizeMetric: [...] }
         */
        exposureListMap: {
            type: Object,
            default() {
                return {};
            },
        },
        /**
         * Exposure selected field from WidgetSettingPanel (unified)
         * { [filterKey]: {}, ... } (keyed by GRAPH_FILTER_TYPES)
         */
        exposureSelectedFieldMap: {
            type: Object,
            default() {
                return {};
            },
        },
        /**
         * If set, auto-open the second panel for this exposure list key
         * (e.g. 'primaryMetric', 'breakdown')
         */
        pendingExposureListKey: {
            type: String,
            default: null,
        },
        /**
         * availableFields props
         */
        selectedSource: {
            type: Array,
            default() {
                return [];
            },
        },
        currentBreakdownIds: {
            type: Array,
            default() {
                return [];
            },
        },
        isAnalytic: {
            type: Boolean,
            default: false,
        },
        currentViewId: Number | String,
        teamspaceId: Number,
        isTemplateView: Boolean,
    },
    data() {
        return {
            activeDropbox: '',
        };
    },
    created() {
        // Per-axis BehaviorSubject for exposure setting second panels
        this._axisSecondPanelVisibility$ = {
            [YAXIS]: new BehaviorSubject(false),
            [XAXIS]: new BehaviorSubject(false),
            [BUBBLE_SIZE]: new BehaviorSubject(false),
            [BREAKDOWN]: new BehaviorSubject(false),
            [DATE_BREAKDOWN]: new BehaviorSubject(false),
            [COMBO_YAXIS_1]: new BehaviorSubject(false),
            [COMBO_YAXIS_2]: new BehaviorSubject(false),
            [COMBO_YAXIS_3]: new BehaviorSubject(false),
        };
    },
    watch: {
        pendingExposureListKey: {
            immediate: true,
            handler(key) {
                if (!key) return;

                const filterKey = EXPOSURE_LIST_KEY_TO_FILTER_KEY[key];
                if (!filterKey) return;

                const subject$ = this._axisSecondPanelVisibility$?.[filterKey];
                if (subject$) {
                    this.$nextTick(() => {
                        subject$.next(true);
                    });
                }
            },
        },
    },
    computed: {
        ...mapGetters('newDashboard', ['aliasesByIndex']),
        ...mapGetters('user', ['pricePlanSettings', 'hasOnlyAvailableWidgets']),
        selectedGraphType: R.pathOr('', ['graphOptions', 'selectedGraphType']),
        isColumnLikeGraph: R.compose(
            R.includes(R.__, [BAR, LINE, HORIZONTAL_BAR]),
            R.pathOr('', ['graphOptions', 'selectedGraphType'])
        ),
        filteredGraphWidgets() {
            const { pricePlanSettings, hasOnlyAvailableWidgets } = this;
            const { availableWidgetTypes = [] } = pricePlanSettings;

            const availableGraphWidgets = R.compose(
                R.filter(Boolean),
                R.pluck('subType')
            )(availableWidgetTypes);

            return hasOnlyAvailableWidgets && availableGraphWidgets.length
                ? R.without(availableGraphWidgets, ALL_CHART_TYPES)
                : [];
        },
        yAxisTitle() {
            const { selectedGraphType } = this;

            switch (selectedGraphType) {
                case WORD_CLOUD:
                    return this.$t('NEW_DASHBOARD.graph_wordCloud_YAxis');
                case HORIZONTAL_BAR:
                    return this.$t('NEW_DASHBOARD.graph_dropdown_x_axis');
                case PIE:
                case DONUT:
                case CHOROPLETH_MAP:
                    return this.$t('NEW_DASHBOARD.graph_dropdown_metric');
                default:
                    return this.$t('NEW_DASHBOARD.graph_dropdown_y_axis');
            }
        },
        dateBreakdownTitle() {
            const { selectedGraphType } = this;

            switch (selectedGraphType) {
                case HORIZONTAL_BAR:
                    return this.$t('NEW_DASHBOARD.graph_dropdown_y_axis');
                default:
                    return this.$t('NEW_DASHBOARD.graph_dropdown_x_axis');
            }
        },
        selectedBreakdownId: R.compose(
            R.head,
            pluckFieldKey,
            R.propOr([], 'breakdownList')
        ),
        selectedGeoMapType() {
            const { selectedBreakdownId } = this;
            return selectedBreakdownId === STATE_US_ONLY_BREAKDOWN
                ? GEO_MAP_TYPES.US_STATE
                : GEO_MAP_TYPES.WORLD;
        },
    },
    methods: {
        ...mapActions('newDashboard', ['saveWidget']),
        getSelectedOptionStatus(key) {
            // GRAPH, GEO_BREAKDOWN, GEO_MAP_TYPE는 항상 NORMAL
            switch (key) {
                case XAXIS:
                case YAXIS:
                case BUBBLE_SIZE: {
                    return ITEM_STATUS.NORMAL;
                }
                case DATE_BREAKDOWN: {
                    return ITEM_STATUS.NORMAL;
                }
                default:
                    return ITEM_STATUS.NORMAL;
            }
        },
        getCustomArrowIcon(key) {
            switch (key) {
                case XAXIS:
                case YAXIS:
                case BUBBLE_SIZE:
                case DATE_BREAKDOWN:
                    return this.secondPanelLoading ? (
                        <RingLoader size={16} />
                    ) : null;
                default:
                    return null;
            }
        },
        setDropdownVisibility(key) {
            this.activeDropbox = this.activeDropbox === key ? '' : key;
        },
        getOptions(key) {
            const {
                graphOptions,
                filteredGraphWidgets,
                dateBreakdownTitle,
                yAxisTitle,
                selectedBreakdownId,
                selectedGeoMapType,
            } = this;

            const {
                selectedGraphType,
                selectedMetric,
                selectedxAxis,
                selectedBubbleSize = '',
                dateBreakdown = 'autoTime',
            } = graphOptions;

            switch (key) {
                /**
                 * optionList: Object[]
                 * selectedOption: String (custom breakdown is Number)
                 */
                case XAXIS: {
                    return {
                        title: this.$t('NEW_DASHBOARD.graph_dropdown_x_axis'),
                        optionList: [],
                        selectedOption: selectedxAxis,
                    };
                }
                case YAXIS:
                    return {
                        title: yAxisTitle,
                        optionList: [],
                        selectedOption: selectedMetric,
                    };
                case BUBBLE_SIZE:
                    return {
                        title: this.$t(
                            'NEW_DASHBOARD.graph_dropdown_bubble_size'
                        ),
                        optionList: [],
                        selectedOption: selectedBubbleSize,
                    };
                case BREAKDOWN: {
                    return {
                        title: this.$t(
                            'NEW_DASHBOARD.graph_dropdown_breakdown'
                        ),
                        optionList: [],
                        selectedOption: selectedBreakdownId,
                    };
                }
                case DATE_BREAKDOWN: {
                    return {
                        title: dateBreakdownTitle,
                        optionList: [],
                        selectedOption:
                            dateBreakdown === 'none'
                                ? Number(selectedBreakdownId) ||
                                  selectedBreakdownId
                                : dateBreakdown,
                    };
                }

                /**
                 * optionList: String[]
                 * selectedOption: String
                 */
                case GRAPH: {
                    return {
                        title: this.$t('NEW_DASHBOARD.graph_type'),
                        optionList: ALL_CHART_TYPES,
                        selectedOption: selectedGraphType,
                        blockedOptions: filteredGraphWidgets,
                    };
                }
                case GEO_BREAKDOWN:
                    return {
                        title: this.$t(
                            'NEW_DASHBOARD.graph_dropdown_geo_breakdown'
                        ),
                        optionList: GEO_BREAKDOWN_LIST,
                        selectedOption: selectedBreakdownId,
                    };
                case GEO_MAP_TYPE:
                    return {
                        title: this.$t(
                            'NEW_DASHBOARD.graph_dropdown_geo_map_type'
                        ),
                        optionList: [selectedGeoMapType],
                        selectedOption: selectedGeoMapType,
                        disabled: true,
                    };
            }
        },
        getTranslator(key) {
            switch (key) {
                case XAXIS:
                case YAXIS:
                case BUBBLE_SIZE: {
                    return this.getMetricName;
                }
                case BREAKDOWN:
                case DATE_BREAKDOWN: {
                    return this.getBreakdownName;
                }
                case GRAPH:
                    return (value) => this.$t(`NEW_DASHBOARD.graph_${value}`);
                case GEO_BREAKDOWN: {
                    return (value) => this.getBreakdownName({ key: value });
                }
                case GEO_MAP_TYPE: {
                    return (value) =>
                        this.translateOr(
                            `NEW_DASHBOARD.geo_map_type_${value}`,
                            capitalize(value)
                        );
                }
                default:
                    return R.identity;
            }
        },
        getHasSearchBar(key) {
            return key !== GEO_BREAKDOWN && key !== GEO_MAP_TYPE;
        },

        /** [TODO] 확인필요
         * exposureStrings 상태에 따라 currentList 결정
         *
         * - null/undefined: 미설정 상태 → defaultFieldList를 선택된 것으로 표시
         *   selectedField가 defaultFieldList에 없으면 맨 앞에 추가
         * - []: 명시적 빈 상태 → 아무것도 선택 안 함
         * - string[]: 저장된 값 그대로 사용
         */
        /**
         * exposureStrings → { key }[] format (metric)
         * null/undefined: defaultFieldList 기반, selectedField가 없으면 앞에 추가
         */
        makeMetricCurrentList(
            exposureStrings,
            defaultFieldList,
            selectedField
        ) {
            let strings;
            if (!Array.isArray(exposureStrings)) {
                strings = [...defaultFieldList];
                const selectedKey = selectedField?.[FIELD_KEY];
                if (
                    selectedKey &&
                    !strings.map(String).includes(String(selectedKey))
                ) {
                    strings.unshift(selectedKey);
                }
            } else {
                strings = exposureStrings;
            }
            return strings.map(makeMetricFormat);
        },
        /**
         * exposureStrings → { key }[] format (breakdown)
         */
        makeBreakdownCurrentList(
            exposureStrings,
            defaultFieldList,
            selectedField
        ) {
            let strings;
            if (!Array.isArray(exposureStrings)) {
                strings = [...defaultFieldList];
                const selectedKey = selectedField?.[FIELD_KEY];
                if (
                    selectedKey &&
                    !strings.map(String).includes(String(selectedKey))
                ) {
                    strings.unshift(selectedKey);
                }
            } else {
                strings = exposureStrings;
            }
            return strings.map(makeBreakdownFormat);
        },
        /**
         * MetricExposureSetting 렌더링 (YAXIS, XAXIS, BUBBLE_SIZE)
         */
        renderMetricExposure(key) {
            const { title } = this.getOptions(key);

            const {
                fieldListKey,
                defaultFieldList,
                fixedOptions = [],
            } = EXPOSURE_KEY_CONFIG[key];

            const selectedField = this.exposureSelectedFieldMap[key] ?? '';
            const exposureStrings = this.exposureListMap[fieldListKey];

            const props = {
                attrs: {
                    optionTitle: title,
                    panelKey: FIELD_PANEL_KEY.GRAPH_METRIC_EXPOSURE(key),

                    // Context props (forwarded from parent)
                    selectedSource: this.selectedSource,
                    currentBreakdownIds: this.currentBreakdownIds,
                    widgetType: this.widgetType,
                    currentViewId: this.currentViewId,
                    teamspaceId: this.teamspaceId,
                    isAnalytic: this.isAnalytic,
                    graphOptions: this.graphOptions,

                    // MetricQuickControl-specific props
                    exposureListKey: fieldListKey,
                    exposureList: exposureStrings,
                    selectedField,
                    defaultList: defaultFieldList,
                    fixedList: fixedOptions,

                    isTemplateView: this.isTemplateView,
                },
                on: {
                    'update:exposureListMap': ({ key: eKey, value }) => {
                        this.$emit('update:exposureListMap', {
                            key: eKey,
                            value,
                        });
                    },
                    'update:exposureSelectedFieldMap': (stringKey) => {
                        this.$emit('update:exposureSelectedFieldMap', {
                            exposureListKey: key,
                            value: stringKey,
                        });
                    },
                },
            };

            return <MetricQuickControl {...props} />;
        },
        /**
         * ComboMetricExposureSetting 렌더링 (yAxis1, yAxis2, yAxis3)
         *
         * exposureKey: 'comboMetric1' | 'comboMetric2' | 'comboMetric3'
         * fieldListKey: exposureFieldList에서 실제 리스트를 가져올 key
         *   - comboMetric1 → 'primaryMetric' (리스트 공유)
         *   - comboMetric2 → 'comboMetric2'
         *   - comboMetric3 → 'comboMetric3'
         */
        renderComboMetricExposure(filterKey) {
            const {
                fieldListKey,
                defaultFieldList,
                fixedOptions = [],
            } = EXPOSURE_KEY_CONFIG[filterKey];
            const yAxisNumber = filterKey.slice(-1); // COMBO_YAXIS_1 last char = '1'
            const title = `Y-axis ${yAxisNumber}`;

            const selectedField =
                this.exposureSelectedFieldMap[filterKey] || {};
            const exposureStrings = this.exposureListMap[fieldListKey];

            const props = {
                attrs: {
                    optionTitle: title,
                    panelKey: FIELD_PANEL_KEY.COMBO_METRIC_EXPOSURE(filterKey),

                    // Context props (forwarded from parent)
                    selectedSource: this.selectedSource,
                    currentBreakdownIds: this.currentBreakdownIds,
                    widgetType: this.widgetType,
                    currentViewId: this.currentViewId,
                    teamspaceId: this.teamspaceId,
                    isAnalytic: this.isAnalytic,
                    graphOptions: this.graphOptions,

                    // ComboMetricQuickControl-specific props
                    exposureListKey: fieldListKey,
                    exposureList: exposureStrings,
                    selectedField, // { key, type, axisPosition }
                    defaultList: defaultFieldList,
                    fixedList: fixedOptions,

                    isTemplateView: this.isTemplateView,
                },
                on: {
                    'update:exposureListMap': ({ key: eKey, value }) => {
                        this.$emit('update:exposureListMap', {
                            key: eKey,
                            value,
                        });
                    },
                    'update:exposureSelectedFieldMap': (value) => {
                        // exposureSelectedFieldMap는 filterKey로 저장 (축별 독립)
                        this.$emit('update:exposureSelectedFieldMap', {
                            exposureListKey: filterKey,
                            value,
                        });
                    },
                },
            };

            return <ComboMetricQuickControl {...props} />;
        },
        /**
         * BreakdownQuickControl 렌더링 (BREAKDOWN, DATE_BREAKDOWN)
         */
        renderBreakdownExposure(key) {
            const { title } = this.getOptions(key);
            const {
                fieldListKey,
                defaultFieldList,
                fixedOptions = [],
            } = EXPOSURE_KEY_CONFIG[key];

            // isDateBreakdownNone: column-like 차트에서 dateBreakdown='none'일 때
            // DATE_BREAKDOWN 드롭다운에 dimensionId를, BREAKDOWN 드롭다운에 'all'을 표시
            const { dateBreakdown = 'autoTime' } = this.graphOptions;
            const isDateBreakdownNone =
                this.isColumnLikeGraph && dateBreakdown === 'none';

            const rawSelectedField = this.exposureSelectedFieldMap[key] ?? '';
            let selectedField;
            if (key === DATE_BREAKDOWN) {
                selectedField = isDateBreakdownNone
                    ? this.exposureSelectedFieldMap[BREAKDOWN] ?? ''
                    : rawSelectedField;
            } else if (key === BREAKDOWN) {
                selectedField = isDateBreakdownNone ? 'all' : rawSelectedField;
            } else {
                selectedField = rawSelectedField;
            }

            const exposureStrings = this.exposureListMap[fieldListKey];
            const isDropdownDisabled = isDateBreakdownNone && key === BREAKDOWN;

            const props = {
                attrs: {
                    optionTitle: title,
                    panelKey: FIELD_PANEL_KEY.GRAPH_BREAKDOWN_EXPOSURE(key),

                    // Context props (forwarded from parent)
                    selectedSource: this.selectedSource,
                    currentBreakdownIds: this.currentBreakdownIds,
                    widgetType: this.widgetType,
                    currentViewId: this.currentViewId,
                    teamspaceId: this.teamspaceId,
                    isAnalytic: this.isAnalytic,
                    graphOptions: this.graphOptions,
                    currentListByType: this.currentListByType,

                    // BreakdownQuickControl-specific props
                    exposureListKey: fieldListKey,
                    exposureList: exposureStrings,
                    selectedField,
                    defaultList: defaultFieldList,
                    fixedList: fixedOptions,
                    dropdownDisabled: isDropdownDisabled,

                    isTemplateView: this.isTemplateView,
                },
                on: {
                    'update:exposureListMap': ({ key: eKey, value }) => {
                        this.$emit('update:exposureListMap', {
                            key: eKey,
                            value,
                        });
                    },
                    'update:exposureSelectedFieldMap': (value) => {
                        if (key === DATE_BREAKDOWN) {
                            const isDateGroup = DATE_GROUP_ROWS.includes(value);
                            if (!isDateGroup && this.isColumnLikeGraph) {
                                // 비-date-group 선택: dateBreakdown='none', dimensionId=value
                                this.updateOption({
                                    key: DATE_BREAKDOWN,
                                    value: 'none',
                                });
                                this.$emit('update:exposureSelectedFieldMap', {
                                    exposureListKey: BREAKDOWN,
                                    value,
                                });
                                this.$emit('update:exposureSelectedFieldMap', {
                                    exposureListKey: DATE_BREAKDOWN,
                                    value: 'none',
                                });
                            } else {
                                // 일반 date-group 선택
                                this.updateOption({
                                    key: DATE_BREAKDOWN,
                                    value,
                                });
                                this.$emit('update:exposureSelectedFieldMap', {
                                    exposureListKey: DATE_BREAKDOWN,
                                    value,
                                });
                            }
                            return;
                        }
                        this.$emit('update:exposureSelectedFieldMap', {
                            exposureListKey: key,
                            value,
                        });
                    },
                },
            };

            return <BreakdownQuickControl {...props} />;
        },
        updateOption({ key, value }) {
            const { graphOptions } = this;

            this.$emit('update', {
                ...graphOptions,
                [key]: value,
            });

            this.activeDropbox = '';
        },
        updateOptionByKey({ key, value }) {
            this.$emit('updateByKey', {
                key,
                value,
            });
        },
        setSelectedBreakdownId(value) {
            const { updateOptionByKey } = this;

            const breakdown = makeBreakdownFormat(value);

            updateOptionByKey({
                key: 'BREAKDOWN',
                value: [breakdown],
            });
        },
        updateDateBreakdown({ key, value }) {
            const { updateOption, setSelectedBreakdownId } = this;

            setSelectedBreakdownId(value);
            updateOption({
                key,
                value: 'none',
            });
        },
        handleConfirm({ key, value }) {
            const { updateOption, setSelectedBreakdownId } = this;

            switch (key) {
                case GEO_BREAKDOWN: {
                    setSelectedBreakdownId(value);
                    this.activeDropbox = '';
                    break;
                }
                case GRAPH: {
                    if (value === CHOROPLETH_MAP) {
                        setSelectedBreakdownId(COUNTRY_BREAKDOWN);
                    }
                    updateOption({ key, value });
                    break;
                }

                default: {
                    break;
                }
            }
        },
        getTooltipTrigger(key) {
            const { selectedGeoMapType } = this;

            if (
                key === GEO_BREAKDOWN &&
                selectedGeoMapType === GEO_MAP_TYPES.WORLD
            ) {
                return this.wrapTooltip({
                    visible: true,
                    content: <InfoLineIcon class="info-line-icon" />,
                    title: this.$t('NEW_DASHBOARD.iso_country_code_info'),
                });
            }

            return null;
        },
        renderOptions(key) {
            // YAXIS, XAXIS, BUBBLE_SIZE: exposure data가 있으면 MetricExposureSetting 렌더링
            if (METRIC_EXPOSURE_AXES.has(key)) {
                return this.renderMetricExposure(key);
            }

            // COMBO_YAXIS_1/2/3: ComboMetricExposureSetting 렌더링
            if (COMBO_METRIC_EXPOSURE_AXES.has(key)) {
                return this.renderComboMetricExposure(key);
            }

            // BREAKDOWN, DATE_BREAKDOWN: BreakdownQuickControl 렌더링
            if (BREAKDOWN_EXPOSURE_AXES.has(key)) {
                return this.renderBreakdownExposure(key);
            }

            const {
                getTranslator,
                getTooltipTrigger,
                setDropdownVisibility,
                activeDropbox,
                getOptions,
                handleConfirm,
                getHasSearchBar,
            } = this;
            const {
                title,
                optionList,
                selectedOption,
                blockedOptions = [],
                disabled = false,
            } = getOptions(key);

            const translator = getTranslator(key);

            const $tooltipTrigger = getTooltipTrigger(key);

            // NEW: status 계산
            const status = this.getSelectedOptionStatus(key);
            const tooltipGenerator = () =>
                status === ITEM_STATUS.INVALID
                    ? this.$t('EDITABLE_DASHBOARD.metric_no_longer_valid')
                    : '';
            const customArrowIcon = this.getCustomArrowIcon(key);

            return (
                <S.OptionWrapper>
                    <S.SettingWrapper>
                        <h1>
                            {title}
                            {$tooltipTrigger}
                        </h1>
                        <S.StatusSelectedBoxWrapper status={status}>
                            <SelectedBox
                                disabled={disabled}
                                selectedOption={selectedOption}
                                translator={translator}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setDropdownVisibility(key);
                                }}
                                tooltipGenerator={tooltipGenerator}
                                customArrowIcon={customArrowIcon}
                            />
                            <S.DropdownBoxWrapper>
                                <DropdownBox
                                    width="100%"
                                    isVisible={activeDropbox === key}
                                    optionList={optionList}
                                    blockedOptions={blockedOptions}
                                    selectedOption={selectedOption}
                                    lineClampOption={DIRECTIVE_CLAMP}
                                    translator={translator}
                                    onConfirm={(value) => {
                                        handleConfirm({ key, value });
                                    }}
                                    onClickOutside={() =>
                                        setDropdownVisibility(key)
                                    }
                                    hasSearchBar={getHasSearchBar(key)}
                                    useLooseSearch={true}
                                    useVirtualScroll={true}
                                />
                            </S.DropdownBoxWrapper>
                        </S.StatusSelectedBoxWrapper>
                    </S.SettingWrapper>
                </S.OptionWrapper>
            );
        },
        /**
         * [TODO] check observe logic
         */
        // observeAnalyticFlag$() {
        //     const { generateWatchNew$: gen$ } = this;

        //     const isAnalytic$ = gen$('isAnalytic').pipe(
        //         skip(1),
        //         switchMap(() =>
        //             gen$('allMetricList').pipe(
        //                 auditTime(150),
        //                 filter(isValueExist),
        //                 distinctUntilChanged(R.equals)
        //             )
        //         )
        //     );
        //     this.$subscribeTo(isAnalytic$, (metricList) => {
        //         let _defaultGraphValue = { ...GRAPH_OPTION_DEFAULT_VALUE };

        //         _defaultGraphValue = this.makeDefaultOptionValue({
        //             metricList,
        //             graphOptions: _defaultGraphValue,
        //         });

        //         const graphType = R.path(
        //             ['graphOptions', 'selectedGraphType'],
        //             this
        //         );
        //         if (graphType) {
        //             _defaultGraphValue = R.assoc(
        //                 'selectedGraphType',
        //                 this.graphOptions.selectedGraphType,
        //                 GRAPH_OPTION_DEFAULT_VALUE
        //             );
        //         }

        //         this.$emit('update', _defaultGraphValue);
        //     });
        // },
        // observeGraphOptions$() {
        //     const { generateWatchNew$: gen$ } = this;

        //     const graphOptions$ = gen$('graphOptions').pipe(
        //         distinctUntilChanged(R.equals)
        //     );

        //     const allMetricList$ = gen$('allMetricList').pipe(
        //         auditTime(150),
        //         filter(isValueExist),
        //         distinctUntilChanged(R.equals)
        //     );

        //     const combined$ = combineLatest(graphOptions$, allMetricList$);

        //     this.$subscribeTo(combined$, ([graphOptions, metricList]) => {
        //         const hasInitialized = R.compose(
        //             R.any(R.equals(undefined)),
        //             R.values,
        //             R.pick(['selectedMetric', 'selectedxAxis'])
        //         )(graphOptions);
        //         if (hasInitialized) {
        //             const defualtOptions = this.makeDefaultOptionValue({
        //                 metricList,
        //                 graphOptions,
        //             });
        //             this.$emit('update', defualtOptions);
        //         }
        //     });
        // },
        /**
         * [TODO] handle makeDefaultOptionValue
         */
        makeDefaultOptionValue({ metricList, graphOptions }) {
            const firstMetric = R.compose(R.defaultTo(''), R.head)(metricList);

            const refinedValue = firstMetric.id
                ? firstMetric.key
                : firstMetric.name;

            const defaultValue = {
                [GRAPH_FILTER_TYPES.YAXIS]: refinedValue,
                [GRAPH_FILTER_TYPES.XAXIS]: refinedValue,
                [GRAPH_FILTER_TYPES.COMBO_METRICS]: {
                    ...graphOptions[GRAPH_FILTER_TYPES.COMBO_METRICS],
                    yAxis1: { metric: refinedValue, type: 'bar' },
                },
            };

            return {
                ...graphOptions,
                ...defaultValue,
            };
        },
    },
    mounted() {
        // this.observeAnalyticFlag$();
        // this.observeGraphOptions$();
    },

    render() {
        const { selectedGraphType, renderOptions } = this;
        const availableMenu = SETTING_OPTIONS_MAP[selectedGraphType];

        return (
            <S.ComponentWrapper>
                {Object.values(availableMenu).map(renderOptions)}
            </S.ComponentWrapper>
        );
    },
};
